import { useTranslation } from 'react-i18next';
import { APP_STORE_URL, PLAY_STORE_URL, isAndroidDevice, isIOSDevice } from '../../utils/device';
import { cn } from '../../utils/cn';

interface AppStoreBadgesProps {
  /** 'default' shows standard badges; 'compact' fits footers and drawers */
  size?: 'default' | 'compact';
  /** Alignment of the badge row */
  align?: 'start' | 'center';
  /** When true on a mobile device, puts the user's current OS store first */
  prioritizeCurrentDevice?: boolean;
  className?: string;
}

export function AppleIcon({ className = 'h-6 w-6' }: { className?: string }) {
  return (
    <svg className={cn('fill-current shrink-0', className)} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.22.67-2.94 1.5-.63.73-1.18 1.87-1.03 2.97 1.12.09 2.27-.58 2.98-1.41z" />
    </svg>
  );
}

export function GooglePlayIcon({ className = 'h-6 w-6' }: { className?: string }) {
  return (
    <svg className={cn('shrink-0', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5.00016 3.27832C4.74316 3.53532 4.60016 3.91832 4.60016 4.41732V19.5823C4.60016 20.0813 4.74316 20.4643 5.00016 20.7213L5.07116 20.7853L13.6842 12.1723V11.8273L5.07116 3.21432L5.00016 3.27832Z" fill="#00F0FF" />
      <path d="M16.556 15.0441L13.6841 12.1721V11.8271L16.557 8.95508L16.626 8.99508L19.998 10.9121C20.962 11.4601 20.962 12.5391 19.998 13.0871L16.626 15.0041C16.626 15.0041 16.556 15.0441 16.556 15.0441Z" fill="#FFC107" />
      <path d="M13.6841 12.0001L5.00012 20.6841C5.32112 21.0051 5.86412 21.0261 6.47112 20.6801L16.5561 15.0441L13.6841 12.0001Z" fill="#FF3D00" />
      <path d="M13.6841 12L16.5561 8.95605L6.47112 3.31987C5.86412 2.97387 5.32112 2.99487 5.00012 3.31587L13.6841 12Z" fill="#4CAF50" />
    </svg>
  );
}

export function AppStoreBadges({
  size = 'default',
  align = 'center',
  prioritizeCurrentDevice = true,
  className,
}: AppStoreBadgesProps) {
  const { t } = useTranslation();
  const isCompact = size === 'compact';
  const androidFirst = prioritizeCurrentDevice && isAndroidDevice() && !isIOSDevice();

  const badgeBaseClass = cn(
    'inline-flex items-center gap-2.5 bg-neutral-950 hover:bg-neutral-900 text-white rounded-xl border border-neutral-800 hover:border-amber-500/40 transition-all shrink-0 justify-center group shadow-sm hover:shadow-md',
    isCompact ? 'px-3.5 py-2' : 'px-4 py-2.5 flex-1 sm:flex-initial min-w-[148px]',
  );

  const iconClass = cn(
    'group-hover:scale-105 transition-transform',
    isCompact ? 'h-5 w-5' : 'h-6 w-6',
  );

  const appStoreBadge = (
    <a
      key="ios"
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={badgeBaseClass}
      dir="ltr"
    >
      <AppleIcon className={cn(iconClass, 'text-white')} />
      <div className="text-left leading-tight">
        <p className={cn('text-neutral-400 uppercase font-sans font-medium tracking-tight', isCompact ? 'text-[9px]' : 'text-[10px]')}>
          {t('plaqueSetup.appStore.downloadOn', 'Download on the')}
        </p>
        <p className={cn('font-semibold font-sans -mt-0.5', isCompact ? 'text-xs' : 'text-sm')}>
          {t('plaqueSetup.appStore.label', 'App Store')}
        </p>
      </div>
    </a>
  );

  const googlePlayBadge = (
    <a
      key="android"
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={badgeBaseClass}
      dir="ltr"
    >
      <GooglePlayIcon className={iconClass} />
      <div className="text-left leading-tight">
        <p className={cn('text-neutral-400 uppercase font-sans font-medium tracking-tight', isCompact ? 'text-[9px]' : 'text-[10px]')}>
          {t('plaqueSetup.googlePlay.getItOn', 'GET IT ON')}
        </p>
        <p className={cn('font-semibold font-sans -mt-0.5', isCompact ? 'text-xs' : 'text-sm')}>
          {t('plaqueSetup.googlePlay.label', 'Google Play')}
        </p>
      </div>
    </a>
  );

  const badges = androidFirst ? [googlePlayBadge, appStoreBadge] : [appStoreBadge, googlePlayBadge];

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2.5',
        align === 'center' ? 'justify-center' : 'justify-start',
        className,
      )}
    >
      {badges}
    </div>
  );
}
